/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { showCharge } from 'js/actions/chargeActions';
import { showGroups } from 'js/actions/groupActions';
import { showMeters } from 'js/actions/meterActions';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Add, Delete, Send } from '@mui/icons-material';
import { sendHistoricalReport } from 'js/actions/reportsActions';

export default function TotalsHistorical() {
  // Intl
  const intl = useIntl();
  // Redux
  const dispatch = useDispatch();
  const { register, handleSubmit } = useForm();
  // Redux state
  const { user } = useSelector((state) => state.user);
  const { corporatives } = useSelector((state) => state.corporatives);
  const { meters } = useSelector((state) => state.meters);
  const { groups } = useSelector((state) => state.groups);
  const { charges } = useSelector((state) => state.charges);
  // State
  const [corporative, setCorporative] = useState();
  const [meter, setMeter] = useState('');
  const [group, setGroup] = useState([]);
  const [charge, setCharge] = useState([]);
  const [contacts, setContacts] = useState([{ name: '', email: '' }]);
  const [blocking, setBlocking] = useState(false);

  // Handlers
  const handleGroupChange = (event) => {
    // Sets the selected corporative and resets meter, group and charge states
    // Calls the showMeters action to fetch meters based on the selected corporative
    const { value } = event.target;
    setCorporative(value);
    setMeter([]);
    setCharge([]);
    setGroup([]);
    dispatch(showMeters(value));
  };

  const handleMeter = (event) => {
    // Sets the selected meter and resets group and charge states
    // Calls the showGroups action to fetch groups based on the selected meter
    const { value } = event.target;
    setMeter(value);
    setGroup([]);
    setCharge([]);
    dispatch(showGroups(value));
  };

  const handleGroup = (event) => {
    // Adds the selected group to the group state
    const { value } = event.target;
    setGroup(value);
    setCharge([]);
    // If the value is an array with one element, set the charge to that element
    // If the value is an array with more than one element, set the charge to 'TODAS'
    if (value.length === 1) {
      dispatch(showCharge(value[0]));
    } else {
      setCharge(['TODAS']);
      dispatch(showCharge(0));
    }
  };

  const handleCharge = (event) => {
    // Adds the selected charge to the charge state
    const { value } = event.target;
    // If the value is 'TODAS', set the charge to 'TODAS'
    // If the value is an array with one element, set the charge to that element
    // If the value is an array with more than one element, set the charge to 'TODAS'
    if (group.length === 1) {
      // If the value is an array with one element, allowed multiple selection
      setCharge(typeof value === 'string' ? [value] : value);
    } else {
      // If there are multiple groups selected, set the charge to 'TODAS'
      setCharge(['TODAS']);
    }
  };

  const handleAddContact = () => {
    // Add a new contact to the contacts array
    setContacts([...contacts, { name: '', email: '' }]);
  };

  const handleDeleteContact = (index) => {
    // Check if there is only one contact left
    // If there is only one contact left, do not delete it
    const newContacts = [...contacts];
    newContacts.splice(index, 1);
    setContacts(newContacts);
  };

  const handleContactChange = (index, event) => {
    // Update the contact name or email based on the index
    // and the name of the input field (name or email)
    const { name, value } = event.target;
    const newContacts = [...contacts];
    newContacts[index][name] = value;
    setContacts(newContacts);
  };

  // Get groups, charges and contacts
  const dataSend = {
    group_ids: group,
    charge_ids: charge,
    contacts: contacts,
  };

  // endBlocking function
  function endBlocking() {
    // Set blocking to false
    setBlocking(false);
  }

  // Form submit
  const onSubmit = (data) => {
    // Set blocking to true
    setBlocking(true);
    // Handle form submission here
    dispatch(sendHistoricalReport(dataSend, endBlocking));
    // Reset form
    setCorporative('');
    setMeter('');
    setGroup([]);
    setCharge([]);
    setContacts([{ name: '', email: '' }]);
  };

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Disabled add contact button if last contact name is empty and email is empty or invalid
  const lastContact = contacts[contacts.length - 1];
  const isDisabled =
    lastContact.name === '' ||
    lastContact.email === '' ||
    !emailRegex.test(lastContact.email);

  // Disable submit button if corporative, meter, group or charge is empty
  const isSubmitDisabled =
    corporative === undefined ||
    meter === '' ||
    group.length === 0 ||
    charge === '' ||
    contacts.some(
      (contact) =>
        contact.name === '' ||
        contact.email === '' ||
        !emailRegex.test(contact.email)
    );

  // Sort corporatives by name
  const shortCorporatives = () => {
    const shortArray = [...corporatives];
    return shortArray.sort((a, b) =>
      a.name !== b.name ? (a.name < b.name ? -1 : 1) : 0
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        bgcolor: 'white',
        p: 4,
        borderRadius: 1,
        boxShadow: 1,
      }}
      width="100%"
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ lg: 3, md: 6, xs: 6 }}>
            <FormControl
              variant="outlined"
              fullWidth={true}
              sx={{ width: '100%' }}
            >
              <InputLabel>
                {intl.formatMessage({ id: 'app.reports.totReports.corpLbl' })}
              </InputLabel>
              <Select
                id="demo-simple-select-outlined"
                {...register('corporative')}
                label={intl.formatMessage({
                  id: 'app.reports.totReports.corpLbl',
                })}
                onChange={(event) => handleGroupChange(event)}
                value={corporative}
              >
                {shortCorporatives().map((corporative) => (
                  <MenuItem key={corporative.id} value={corporative.id}>
                    {corporative.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ lg: 3, md: 6, xs: 6 }}>
            <FormControl
              variant="outlined"
              fullWidth={true}
              sx={{ width: '100%' }}
            >
              <InputLabel>
                {intl.formatMessage({
                  id: 'app.reports.totReports.meterLbl',
                })}
              </InputLabel>
              <Select
                id="demo-simple-select-outlined"
                {...register('meter')}
                label={intl.formatMessage({
                  id: 'app.reports.totReports.meterLbl',
                })}
                onChange={(event) => handleMeter(event)}
                value={meter}
              >
                {meters.map((meter) => (
                  <MenuItem key={meter.id} value={meter.id}>
                    {meter.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ lg: 3, md: 6, xs: 6 }}>
            <FormControl
              variant="outlined"
              fullWidth={true}
              sx={{ width: '100%' }}
            >
              <InputLabel>
                {intl.formatMessage({
                  id: 'app.reports.totReports.groupLbl',
                })}
              </InputLabel>
              <Select
                id="demo-simple-select-outlined"
                {...register('group_ids')}
                multiple
                label={intl.formatMessage({
                  id: 'app.reports.totReports.groupLbl',
                })}
                onChange={(event) => handleGroup(event)}
                value={group}
                renderValue={(value) => {
                  if (!value.length) return '';
                  return value
                    .map((id) => groups.find((group) => group.id === id).name)
                    .join(', ');
                }}
              >
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    <Checkbox checked={group.indexOf(g.id) > -1} />
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ lg: 3, md: 6, xs: 6 }}>
            <FormControl
              variant="outlined"
              fullWidth={true}
              sx={{ width: '100%' }}
            >
              <InputLabel>
                {intl.formatMessage({
                  id: 'app.reports.hourReports.chargeLbl',
                })}
              </InputLabel>
              <Select
                id="demo-simple-select-outlined"
                {...register('charge_ids')}
                label={intl.formatMessage({
                  id: 'app.reports.hourReports.chargeLbl',
                })}
                multiple={group.length === 1}
                onChange={handleCharge}
                value={charge}
                renderValue={(value) => {
                  if (!value.length) return '';
                  if (value[0] === 'TODAS') return 'Todas';
                  return value
                    .map((id) => {
                      const found = charges.find((charge) => charge.id === id);
                      return found ? found.name : '';
                    })
                    .join(', ');
                }}
              >
                {group.length === 1 &&
                  charges.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={charge.includes(c.id)} />
                      {c.name}
                    </MenuItem>
                  ))}

                {group.length > 1 && (
                  <MenuItem key="TODAS" value="TODAS">
                    Todas
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid2>

          {/* Contacts */}
          {contacts.map((contact, index) => (
            <Grid2
              key={index}
              size={{ lg: 12, sm: 6 }}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <TextField
                label="Nombre"
                variant="outlined"
                name="name"
                value={contact.name}
                onChange={(event) => handleContactChange(index, event)}
                sx={{ width: '50%' }}
              />
              <TextField
                label="Correo electrónico"
                variant="outlined"
                name="email"
                value={contact.email}
                error={!!contact.email && !emailRegex.test(contact.email)}
                helperText={
                  !!contact.email && !emailRegex.test(contact.email)
                    ? 'Correo electrónico inválido'
                    : ''
                }
                onChange={(event) => handleContactChange(index, event)}
                sx={{ width: '50%' }}
              />
              {contacts.length > 1 && (
                <Button
                  type="button"
                  onClick={() => handleDeleteContact(index)}
                  color="error"
                  variant="outlined"
                  size="large"
                  sx={{ padding: '12px' }}
                  startIcon={<Delete />}
                >
                  Eliminar
                </Button>
              )}
            </Grid2>
          ))}
          <Button
            type="button"
            onClick={handleAddContact}
            variant="outlined"
            color="secondary"
            size="large"
            disabled={isDisabled}
            startIcon={<Add />}
            sx={{ width: '100%', height: '45px', marginTop: '20px' }}
          >
            Agregar contacto
          </Button>

          <Grid2 size={12}>
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              disabled={isSubmitDisabled}
              fullWidth={true}
              loading={blocking}
              loadingPosition="start"
              size="large"
              sx={{ width: '100%', height: '45px' }}
              startIcon={<Send />}
            >
              {blocking ? 'Enviando...' : 'Enviar reporte'}
            </Button>
          </Grid2>
        </Grid2>
      </form>
    </Box>
  );
}
